import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CustomArrayChipField from "components/CustomArrayChipField";
import CustomDateField from "components/CustomDateField";
import CustomTimeField from "components/CustomTimeField";
import SanitizedGrid from "components/SanitizedGrid";
import { ETypeHoliday } from "pages/holidays/const";
import React, { useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FunctionField,
  Loading,
  ResourceContextProvider,
  SearchInput,
  ShowProps,
  TextField,
  useDataProvider,
  useNotify,
} from "react-admin";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomPagination from "../../components/CustomPagination";

const useStyles = makeStyles({
  MuiAccordionRoot: {
    "&.Mui-disabled": {
      backgroundColor: "transparent",
    },
  },
  MuiAccordionSummary: {
    minHeight: "64px",
    "& .MuiAccordionSummary-content": {
      margin: "10px 0px 20px",
    },
    "&.Mui-disabled": {
      opacity: 1,
    },
  },
});

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const CcaSchedule = (props: ShowProps) => {
  const classes = useStyles();
  const provider = useDataProvider();
  const notify = useNotify();
  const [ccaAcademicYearId, setCcaAcademicYearId] = useState<number>();
  const [ccaAcademicYears, setCcaAcademicYears] = useState<ICcaAcademicYear[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);

  // useEffect for select all cca academic years
  useEffect(() => {
    if (props.id) {
      provider
        .getList("ccaAcademicYears", {
          filter: { SchoolId: Number(props.id), Status: "Active" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          setLoading(false);
          setCcaAcademicYears(data as ICcaAcademicYear[]);
        })
        .catch((error: any) => {
          setLoading(false);
          notify(
            `Cannot load CCA Academic Year, error: ${error.message}`,
            "warning"
          );
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  // useEffect for set default CCA AcademicYearId
  // The default is active CCA AcademicYearId
  useEffect(() => {
    if (ccaAcademicYears && ccaAcademicYears.length > 0) {
      setCcaAcademicYearId(ccaAcademicYears[0]?.id);
    }
  }, [ccaAcademicYears]);

  if (loading) {
    return <Loading />;
  }

  return (
    <FunctionField
      render={(record: any) =>
        record.isCcaSettingEnable ? (
          ccaAcademicYears.length > 0 && !loading ? (
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="cca-academic-year-content"
                id="cca-academic-year-header"
                className={classes.MuiAccordionSummary}
              >
                <Typography variant="h6">
                  Current CCA Academic Year : {ccaAcademicYears[0].name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SanitizedGrid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">CCA Academic Terms</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <ResourceContextProvider value="ccaAcademicTerms">
                      <PaddingDenseStyled2List
                        {...props}
                        exporter={false}
                        hasCreate={false}
                        filters={<ThisFilter />}
                        filterDefaultValues={{
                          CcaAcademicYearId: ccaAcademicYearId,
                        }}
                        bulkActionButtons={false}
                        title=" "
                        pagination={<CustomPagination />}
                      >
                        <Datagrid>
                          <TextField source="name" label="Term" />
                          <CustomDateField source="startDate" label="From" />
                          <CustomDateField source="endDate" label="To" />
                        </Datagrid>
                      </PaddingDenseStyled2List>
                    </ResourceContextProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6">CCA Bus Schedules</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <ResourceContextProvider value="busSchedules">
                      <PaddingDenseStyled2List
                        {...props}
                        exporter={false}
                        hasCreate={false}
                        filters={<ThisFilter />}
                        filterDefaultValues={{
                          SchoolId: parseInt(props.id),
                          Type: "Cca",
                        }}
                        bulkActionButtons={false}
                        title=" "
                        pagination={<CustomPagination />}
                      >
                        <Datagrid>
                          <TextField
                            source="name"
                            label="Wave Name"
                            emptyText="-"
                          />
                          <CustomTimeField
                            source="arrivalTime"
                            label="Bus Leave by"
                          />
                          <CustomTimeField
                            source="schoolTime"
                            label="Cca End Time"
                          />
                          <FunctionField
                            label="Days of the Weeks"
                            render={(record: any) => {
                              let days: string = "";

                              if (record.isMonday) {
                                days = days + " Monday,";
                              }
                              if (record.isTuesday) {
                                days = days + " Tuesday,";
                              }
                              if (record.isWednesday) {
                                days = days + " Wednesday,";
                              }
                              if (record.isThursday) {
                                days = days + " Thursday,";
                              }
                              if (record.isFriday) {
                                days = days + " Friday,";
                              }

                              return days.replace(/,(?=[^,]*$)/, "");
                            }}
                          />
                          <CustomArrayChipField
                            source="levels"
                            chipSource="name"
                          />
                        </Datagrid>
                      </PaddingDenseStyled2List>
                    </ResourceContextProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6">CCA Holidays</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      CCA School Holiday
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <ResourceContextProvider value="ccaHolidays">
                      <PaddingDenseStyled2List
                        {...props}
                        exporter={false}
                        hasCreate={false}
                        filters={<ThisFilter />}
                        filterDefaultValues={{
                          SchoolId: Number(props.id),
                          CcaAcademicYearId: ccaAcademicYearId,
                          Type: ETypeHoliday.School,
                        }}
                        bulkActionButtons={false}
                        title=" "
                        pagination={<CustomPagination />}
                      >
                        <Datagrid>
                          <TextField source="name" label="Holiday" />
                          <CustomDateField source="date" />
                        </Datagrid>
                      </PaddingDenseStyled2List>
                    </ResourceContextProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      CCA Public Holiday
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <ResourceContextProvider value="ccaHolidays">
                      <PaddingDenseStyled2List
                        {...props}
                        exporter={false}
                        hasCreate={false}
                        filters={<ThisFilter />}
                        filterDefaultValues={{
                          SchoolId: Number(props.id),
                          CcaAcademicYearId: ccaAcademicYearId,
                          Type: ETypeHoliday.Public,
                        }}
                        bulkActionButtons={false}
                        title=" "
                        pagination={<CustomPagination />}
                      >
                        <Datagrid>
                          <TextField source="name" label="Holiday" />
                          <CustomDateField source="date" />
                        </Datagrid>
                      </PaddingDenseStyled2List>
                    </ResourceContextProvider>
                  </Grid>
                </SanitizedGrid>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Accordion
              className={classes.MuiAccordionRoot}
              disabled
              defaultExpanded
            >
              <AccordionSummary
                aria-controls="cca-academic-year-content"
                id="cca-academic-year-header"
                className={classes.MuiAccordionSummary}
              >
                <Typography variant="h6">
                  No CCA Academic Year Active yet.
                </Typography>
              </AccordionSummary>
            </Accordion>
          )
        ) : (
          <Accordion className={classes.MuiAccordionRoot} disabled>
            <AccordionSummary
              aria-controls="cca-academic-year-content"
              id="cca-academic-year-header"
              className={classes.MuiAccordionSummary}
            >
              <Typography variant="h6">
                No CCA Academic Year yet. CCA Setting is Disabled.
              </Typography>
            </AccordionSummary>
          </Accordion>
        )
      }
    />
  );
};

export default CcaSchedule;
